import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { showSuccess, showError, showWarning } from '../utils/toast';
import CreateExamForm from '../components/CreateExamForm';
import EditExamModal from '../components/EditExamModal';
import ExamsList from '../components/ExamsList';
import DashboardOverview from '../components/DashboardOverview';
import ManageQuestions from '../components/ManageQuestions';
import StudentApprovals from '../components/StudentApprovals';
import ExamResults from '../components/ExamResults';
import AIAnalysis from '../components/AIAnalysis';
import QuestionLibraryManager from '../components/QuestionLibraryManager';
import OrganizationSettings from '../components/OrganizationSettings';

const OrganizationDashboard = () => {
  const navigate = useNavigate();
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgLogo, setOrgLogo] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showExamForm, setShowExamForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingExamId, setEditingExamId] = useState(null);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [currentStep, setCurrentStep] = useState(1); // Step wizard state

  // Exam form state
  const [examForm, setExamForm] = useState({
    name: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    showResultImmediately: false,
    totalQuestions: '',
    totalMarks: '',
    marksPerQuestion: '',
    bannerImage: '',
    visibility: 'organization', // 'public' or 'organization'
    attemptLimit: 'once', // 'once', '2', '3', 'custom', 'unlimited'
    customAttempts: '',
    duration: '', // in minutes
    hasNegativeMarking: false, // Enable/disable negative marking
    negativeMarks: '', // Negative marks per wrong answer (e.g., 0.25, 0.5, 1)
    numberOfSections: 1,
    sections: [{ name: '', questions: '', duration: '' }], // Sections with name, question count, and duration
    hasPerQuestionTimer: false, // Enable/disable per question timer
    timePerQuestion: '', // Time per question in seconds
    useRandomSelection: false, // Enable random question selection mode
    selectedSubjects: [] // Selected subjects for random selection
  });

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAdminAuthenticated');
    if (!isAuthenticated) {
      navigate('/admin-login');
      return;
    }
    setAdminName(localStorage.getItem('adminName') || 'Admin');
    setAdminEmail(localStorage.getItem('adminEmail') || '');
    setOrgName(localStorage.getItem('adminOrg') || 'Organization');
    fetchOrganizationData();
    fetchExams();
    fetchTotalQuestions();

    // Listen for organization updates
    const handleOrgUpdate = () => {
      fetchOrganizationData();
    };
    window.addEventListener('organizationUpdated', handleOrgUpdate);

    return () => {
      window.removeEventListener('organizationUpdated', handleOrgUpdate);
    };
  }, [navigate]);

  const fetchOrganizationData = async () => {
    try {
      const adminId = localStorage.getItem('adminId');
      if (adminId) {
        const docRef = doc(db, 'organizationAdmins', adminId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.logo) {
            setOrgLogo(data.logo);
          }
          if (data.organizationName) {
            setOrgName(data.organizationName);
            localStorage.setItem('adminOrg', data.organizationName);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching organization data:', error);
    }
  };

  const fetchExams = async () => {
    try {
      const adminId = localStorage.getItem('adminId');
      console.log('Fetching exams for adminId:', adminId);
      
      // Fetch ALL exams created by this organization (both public and organization-only)
      const orgQuery = query(
        collection(db, 'exams'), 
        where('organizationId', '==', adminId)
      );
      const orgSnapshot = await getDocs(orgQuery);
      
      const examsList = [];
      
      // Add all organization exams
      orgSnapshot.forEach((doc) => {
        examsList.push({ id: doc.id, ...doc.data() });
      });
      
      console.log('Fetched exams count:', examsList.length);
      console.log('Exams:', examsList);
      
      // Sort by creation date (newest first)
      examsList.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
      
      setExams(examsList);
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  const fetchTotalQuestions = async () => {
    try {
      const adminId = localStorage.getItem('adminId');
      const q = query(collection(db, 'questions'), where('organizationId', '==', adminId));
      const querySnapshot = await getDocs(q);
      setTotalQuestions(querySnapshot.size);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const handleExamFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    // Special handling for totalQuestions - auto-update single section
    if (name === 'totalQuestions' && examForm.numberOfSections === 1) {
      setExamForm({
        ...examForm,
        [name]: newValue,
        sections: [{ name: examForm.sections[0].name || examForm.name || '', questions: newValue }]
      });
      return;
    }
    
    // Auto-calculate total marks based on questions and marks per question (when both are filled)
    // But allow manual override for cases where questions have different marks
    if ((name === 'totalQuestions' || name === 'marksPerQuestion') && examForm.totalMarks === 0) {
      const questions = name === 'totalQuestions' ? parseFloat(value) || 0 : examForm.totalQuestions;
      const marksPerQ = name === 'marksPerQuestion' ? parseFloat(value) || 0 : examForm.marksPerQuestion;
      const calculatedTotal = questions * marksPerQ;
      
      setExamForm({
        ...examForm,
        [name]: newValue,
        totalMarks: calculatedTotal
      });
    } else {
      setExamForm({
        ...examForm,
        [name]: newValue
      });
    }
  };

  const handleSectionsChange = (value) => {
    const numSections = parseInt(value) || 1;
    const newSections = [];
    
    for (let i = 0; i < numSections; i++) {
      if (examForm.sections[i]) {
        // Preserve existing section data
        newSections.push(examForm.sections[i]);
      } else {
        // Create new section with default name
        newSections.push({
          name: numSections === 1 && examForm.name ? examForm.name : `Section ${i + 1}`,
          questions: '',
          duration: ''
        });
      }
    }
    
    setExamForm({
      ...examForm,
      numberOfSections: numSections,
      sections: newSections
    });
  };

  const handleSectionChange = (index, field, value) => {
    const newSections = [...examForm.sections];
    newSections[index] = {
      ...newSections[index],
      [field]: value
    };
    
    setExamForm({
      ...examForm,
      sections: newSections
    });
  };

  const handleCreateExam = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate start date/time is in the future
      const startDateTime = new Date(`${examForm.startDate}T${examForm.startTime}`);
      const currentDateTime = new Date();
      
      if (startDateTime <= currentDateTime) {
        showWarning('Start date and time must be in the future!');
        setLoading(false);
        return;
      }

      // Validate end date/time is after start date/time
      const endDateTime = new Date(`${examForm.endDate}T${examForm.endTime}`);
      if (endDateTime <= startDateTime) {
        alert('Error: End date and time must be after start date and time!');
        setLoading(false);
        return;
      }

      const adminId = localStorage.getItem('adminId');
      const docRef = await addDoc(collection(db, 'exams'), {
        ...examForm,
        organizationId: adminId,
        organizationName: orgName,
        createdBy: adminEmail,
        createdAt: new Date().toISOString(),
        status: 'draft'
      });
      
      showSuccess('Exam created successfully! Now add questions to the exam.');
      setShowExamForm(false);
      
      // Set the newly created exam as selected and navigate to questions tab
      const newExam = {
        id: docRef.id,
        name: examForm.name,
        ...examForm
      };
      setSelectedExam(newExam);
      setActiveTab('questions');
      
      // Reset form and step
      setCurrentStep(1);
      setExamForm({
        name: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        showResultImmediately: false,
        totalQuestions: '',
        totalMarks: '',
        marksPerQuestion: '',
        bannerImage: '',
        visibility: 'organization',
        attemptLimit: 'once',
        customAttempts: '',
        duration: '',
        hasNegativeMarking: false,
        negativeMarks: '',
        numberOfSections: 1,
        sections: [{ name: '', questions: '', duration: '' }],
        hasPerQuestionTimer: false,
        timePerQuestion: '',
        useRandomSelection: false,
        selectedSubjects: []
      });
      fetchExams();
    } catch (error) {
      console.error('Error creating exam:', error);
      showError('Error creating exam. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditExam = (exam) => {
    setEditingExamId(exam.id);
    setExamForm({
      name: exam.name,
      startDate: exam.startDate,
      startTime: exam.startTime,
      endDate: exam.endDate,
      endTime: exam.endTime,
      showResultImmediately: exam.showResultImmediately,
      totalQuestions: exam.totalQuestions,
      totalMarks: exam.totalMarks,
      marksPerQuestion: exam.marksPerQuestion || '',
      bannerImage: exam.bannerImage || '',
      visibility: exam.visibility || 'organization',
      attemptLimit: exam.attemptLimit || 'once',
      customAttempts: exam.customAttempts || '',
      duration: exam.duration || '',
      hasNegativeMarking: exam.hasNegativeMarking || false,
      negativeMarks: exam.negativeMarks || '',
      numberOfSections: exam.numberOfSections || 1,
      sections: exam.sections || [{ name: '', questions: '', duration: '' }],
      hasPerQuestionTimer: exam.hasPerQuestionTimer || false,
      timePerQuestion: exam.timePerQuestion || '',
      useRandomSelection: exam.useRandomSelection || false,
      selectedSubjects: exam.selectedSubjects || []
    });
    setShowEditModal(true);
  };

  const handleUpdateExam = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      
      // Validate end date/time is after start date/time
      const startDateTime = new Date(`${examForm.startDate}T${examForm.startTime}`);
      const endDateTime = new Date(`${examForm.endDate}T${examForm.endTime}`);
      if (endDateTime <= startDateTime) {
        showWarning('End date and time must be after start date and time!');
        setLoading(false);
        return;
      }

      const examRef = doc(db, 'exams', editingExamId);
      await updateDoc(examRef, {
        ...examForm,
        updatedAt: new Date().toISOString()
      });
      
      showSuccess('Exam updated successfully!');
      setShowExamForm(false);
      setShowEditModal(false);
      setEditingExamId(null);
      setExamForm({
        name: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        showResultImmediately: false,
        totalQuestions: '',
        totalMarks: '',
        marksPerQuestion: '',
        bannerImage: '',
        visibility: 'organization',
        attemptLimit: 'once',
        customAttempts: '',
        duration: '',
        hasNegativeMarking: false,
        negativeMarks: '',
        numberOfSections: 1,
        sections: [{ name: '', questions: '' }]
      });
      fetchExams();
    } catch (error) {
      console.error('Error updating exam:', error);
      showError('Error updating exam. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditingExamId(null);
    setShowExamForm(false);
    setShowEditModal(false);
    setExamForm({
      name: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      showResultImmediately: false,
      totalQuestions: '',
      totalMarks: '',
      marksPerQuestion: '',
      bannerImage: '',
      visibility: 'organization',
      attemptLimit: 'once',
      customAttempts: '',
      duration: '',
      hasNegativeMarking: false,
      negativeMarks: '',
      numberOfSections: 1,
      sections: [{ name: '', questions: '' }]
    });
  };

  const hasExamStarted = () => {
    if (!editingExamId || !examForm.startDate || !examForm.startTime) return false;
    const startDateTime = new Date(`${examForm.startDate}T${examForm.startTime}`);
    const currentDateTime = new Date();
    return currentDateTime >= startDateTime;
  };

  const handleStatusChange = async (examId, newStatus) => {
    try {
      // If changing to active, validate questions first
      if (newStatus === 'active') {
        const validationResult = await validateExamForActivation(examId);
        if (!validationResult.isValid) {
          showError(validationResult.message);
          return;
        }
      }

      const { doc, updateDoc } = await import('firebase/firestore');
      const examRef = doc(db, 'exams', examId);
      await updateDoc(examRef, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      showSuccess(`Exam status updated to ${newStatus.toUpperCase()}`);
      fetchExams();
    } catch (error) {
      console.error('Error updating status:', error);
      showError('Error updating status. Please try again.');
    }
  };

  const validateExamForActivation = async (examId) => {
    try {
      // Get exam details
      const { doc, getDoc } = await import('firebase/firestore');
      const examDoc = await getDoc(doc(db, 'exams', examId));
      
      if (!examDoc.exists()) {
        return { isValid: false, message: 'Exam not found' };
      }

      const exam = examDoc.data();
      
      // Skip question validation for random selection mode
      if (exam.useRandomSelection) {
        // For random selection, just check if subjects are selected
        if (!exam.selectedSubjects || exam.selectedSubjects.length === 0) {
          return { 
            isValid: false, 
            message: 'Cannot activate exam without selected subjects. Please edit exam to add subjects.' 
          };
        }
        // Random selection exams are valid without manually added questions
        return { isValid: true };
      }
      
      const expectedQuestions = parseInt(exam.totalQuestions);
      const expectedTotalMarks = parseFloat(exam.totalMarks);

      // Get questions for this exam
      const questionsQuery = query(
        collection(db, 'questions'),
        where('examId', '==', examId)
      );
      const questionsSnapshot = await getDocs(questionsQuery);
      
      const actualQuestions = questionsSnapshot.size;
      
      // Calculate actual total marks
      let actualTotalMarks = 0;
      questionsSnapshot.forEach((doc) => {
        actualTotalMarks += parseFloat(doc.data().marks || 0);
      });

      // Validation checks
      if (actualQuestions === 0) {
        return { 
          isValid: false, 
          message: 'Cannot activate exam without questions. Please add questions first.' 
        };
      }

      if (actualQuestions !== expectedQuestions) {
        return { 
          isValid: false, 
          message: `Question count mismatch! Expected: ${expectedQuestions}, Added: ${actualQuestions}. Please ${actualQuestions < expectedQuestions ? 'add ' + (expectedQuestions - actualQuestions) + ' more question(s)' : 'remove ' + (actualQuestions - expectedQuestions) + ' question(s)'}.` 
        };
      }

      if (Math.abs(actualTotalMarks - expectedTotalMarks) > 0.01) {
        return { 
          isValid: false, 
          message: `Total marks mismatch! Expected: ${expectedTotalMarks}, Actual: ${actualTotalMarks}. Please adjust question marks to match the total.` 
        };
      }

      return { isValid: true };
    } catch (error) {
      console.error('Validation error:', error);
      return { isValid: false, message: 'Validation failed: ' + error.message };
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isAdminAuthenticated');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminName');
    localStorage.removeItem('adminOrg');
    localStorage.removeItem('adminId');
    navigate('/admin-login');
  };

  const menuItems = [
    { 
      id: 'overview', 
      name: 'Overview', 
      icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
    },
    { 
      id: 'exams', 
      name: 'Exams', 
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
    },
    { 
      id: 'library', 
      name: 'Question Library', 
      icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
    },
    { 
      id: 'students', 
      name: 'Student Approvals', 
      icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
    },
    { 
      id: 'results', 
      name: 'Results', 
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
    },
    { 
      id: 'ai-analysis', 
      name: 'AI Analysis', 
      icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
      isNew: true
    },
    { 
      id: 'questions', 
      name: 'Manage Exam Questions', 
      icon: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      hidden: true // Hidden from sidebar, accessed via "Manage Questions" button on exams
    },
    { 
      id: 'settings', 
      name: 'Settings', 
      icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z'
    }
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-b from-indigo-600 to-purple-700 text-white transition-all duration-300 flex flex-col`}>
        {/* Logo Section */}
        <div className="p-4 border-b border-indigo-500">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-white p-2 rounded-lg">
                {orgLogo ? (
                  <img 
                    src={orgLogo} 
                    alt="Organization Logo" 
                    className="h-8 w-8 object-contain"
                  />
                ) : (
                  <svg className="h-8 w-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                )}
              </div>
              {sidebarOpen && (
                <div className="ml-3">
                  <h2 className="font-bold text-lg">{orgName}</h2>
                  <p className="text-xs text-indigo-200">{adminEmail}</p>
                </div>
              )}
            </div>
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white hover:bg-indigo-700 p-2 rounded-lg transition"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sidebarOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.filter(item => !item.hidden).map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center ${sidebarOpen ? 'px-4' : 'px-3 justify-center'} py-3 rounded-lg transition-all duration-200 relative ${
                activeTab === item.id
                  ? 'bg-white text-indigo-600 shadow-lg'
                  : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
            >
              <svg className="h-6 w-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
              </svg>
              {sidebarOpen && (
                <span className="ml-3 flex items-center gap-2">
                  {item.name}
                  {item.isNew && (
                    <span className="bg-yellow-400 text-yellow-900 text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
                      NEW
                    </span>
                  )}
                </span>
              )}
              {!sidebarOpen && item.isNew && (
                <span className="absolute top-1 right-1 bg-yellow-400 w-2 h-2 rounded-full animate-pulse"></span>
              )}
            </button>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-indigo-500">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center ${sidebarOpen ? 'px-4' : 'px-3 justify-center'} py-3 rounded-lg text-indigo-100 hover:bg-red-600 hover:text-white transition-all duration-200`}
          >
            <svg className="h-6 w-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            {sidebarOpen && <span className="ml-3 font-medium">Logout</span>}
          </button>
        </div>

        {/* Powered By */}
        {sidebarOpen && (
          <div className="px-4 py-2 bg-indigo-800 bg-opacity-50">
            <p className="text-xs text-indigo-300 text-center">Powered by <span className="font-bold text-white">ExamPro</span></p>
            <p className="text-xs text-indigo-200 text-center">Online Examination System</p>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {menuItems.find(item => item.id === activeTab)?.name || 'Dashboard'}
                </h1>
                <p className="text-sm text-gray-600 mt-1">Welcome back, {adminName}</p>
              </div>
              <div className="flex items-center gap-4">
                <button className="p-2 hover:bg-gray-100 rounded-lg transition">
                  <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
                    {adminName.charAt(0).toUpperCase()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-6">
        {/* Content Based on Active Tab */}
        {activeTab === 'overview' && (
          <DashboardOverview 
            adminName={adminName} 
            stats={{
              totalExams: exams.filter(e => e.status !== 'draft').length,
              totalStudents: 0, // TODO: Implement students feature
              activeExams: (() => {
                const now = new Date();
                return exams.filter(e => {
                  if (e.status === 'draft' || e.status === 'cancelled') return false;
                  const startDateTime = new Date(`${e.startDate}T${e.startTime}`);
                  const endDateTime = new Date(`${e.endDate}T${e.endTime}`);
                  return now >= startDateTime && now <= endDateTime;
                }).length;
              })(),
              totalQuestions: totalQuestions
            }}
          />
        )}

        {activeTab === 'exams' && (
          <div className="space-y-6">
            {/* Tabbed Interface */}
            <div className="bg-white rounded-xl shadow-md">
              <div className="border-b border-gray-200">
                <div className="flex">
                  <button
                    onClick={() => setShowExamForm(false)}
                    className={`px-6 py-4 font-semibold transition ${
                      !showExamForm
                        ? 'border-b-2 border-indigo-600 text-indigo-600 bg-indigo-50'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                      Manage Exams
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setShowExamForm(true);
                      setCurrentStep(1);
                    }}
                    className={`px-6 py-4 font-semibold transition ${
                      showExamForm
                        ? 'border-b-2 border-green-600 text-green-600 bg-green-50'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create New Exam
                    </span>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {showExamForm && !editingExamId ? (
                  <CreateExamForm
                    examForm={examForm}
                    handleExamFormChange={handleExamFormChange}
                    handleCreateExam={handleCreateExam}
                    handleSectionsChange={handleSectionsChange}
                    handleSectionChange={handleSectionChange}
                    setExamForm={setExamForm}
                    loading={loading}
                    onCancel={() => {
                      setShowExamForm(false);
                      setCurrentStep(1);
                    }}
                    currentStep={currentStep}
                    setCurrentStep={setCurrentStep}
                  />
                ) : (
                  <ExamsList
                    exams={exams}
                    onEdit={handleEditExam}
                    onStatusChange={handleStatusChange}
                    onManageQuestions={(exam) => {
                      setSelectedExam(exam);
                      setActiveTab('questions');
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'students' && (
          <StudentApprovals />
        )}

        {activeTab === 'results' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <ExamResults />
          </div>
        )}

        {activeTab === 'ai-analysis' && (
          <AIAnalysis />
        )}

        {activeTab === 'library' && (
          <QuestionLibraryManager />
        )}

        {activeTab === 'settings' && (
          <OrganizationSettings />
        )}

        {activeTab === 'questions' && (
          <ManageQuestions 
            examId={selectedExam?.id} 
            examName={selectedExam?.name}
          />
        )}
        </main>
      </div>

      {/* Edit Exam Modal */}
      {showEditModal && (
        <EditExamModal
          examForm={examForm}
          handleExamFormChange={handleExamFormChange}
          handleUpdateExam={handleUpdateExam}
          loading={loading}
          onCancel={cancelEdit}
          hasExamStarted={hasExamStarted}
        />
      )}
    </div>
  );
};

export default OrganizationDashboard;
